import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../supabaseClient';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';

/**
 * May this user edit festival maps? Backed by map_operator (migration 126) through
 * the is_map_operator() RPC.
 *
 * The RPC answers false for a request that reaches it without the user's token (auth.uid() is null), and an error used
 * to be read as false too; either was then cached for 10 minutes, so a signed-in operator could be told "Studio is for
 * map operators" (Sam, 2026-10-21; both his accounts have rows). Now an error throws (the query retries), a false is
 * only believed once the client's own session is this user, and only a true is cached for long.
 */
export function useIsMapOperator() {
  const { session } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  return useQuery({
    queryKey: ['map-operator', userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_map_operator');
      if (error) throw error;
      if (data === true) return true;
      // A false is only an answer when the request carried this user's token.
      const { data: now } = await supabase.auth.getSession();
      if (now?.session?.user?.id !== userId) throw new Error('is_map_operator: session not ready');
      const again = await supabase.rpc('is_map_operator');
      if (again.error) throw again.error;
      return again.data === true;
    },
    enabled: Boolean(userId),
    retry: 3,
    retryDelay: (n) => Math.min(500 * 2 ** n, 4000),
    staleTime: (q) => (q.state.data === true ? 10 * 60 * 1000 : 0),
    gcTime: 60 * 60 * 1000,
  });
}
